import * as stylex from "@stylexjs/stylex";
import { AlertCircle, type LucideIcon, RotateCw, Upload, WifiOff } from "lucide-react";
import { copy } from "@/copy";
import "@/theme/tokens.stylex";
import { colors, radii, space } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Button } from "@/ui/Button";
import { Icon } from "@/ui/Icon";
import type { UploadSummary } from "../uploads";
type ShownSummary = Exclude<UploadSummary, {
  kind: "none";
}>;
type UploadRowProps = {
  summary: ShownSummary;
  onRetryAll: () => void;
  onClear: () => void;
};
const ICONS: Record<ShownSummary["kind"], LucideIcon> = {
  offline: WifiOff,
  failed: AlertCircle,
  uploading: Upload
};
export function UploadRow({
  summary,
  onRetryAll,
  onClear
}: UploadRowProps) {
  const offline = summary.kind === "offline";
  return <div role="status" {...{
    0: {
      className: "x78zum5 x1a02dak x6s0dn4 x1cxx1dg x1wk51jd x1wxaq2x xp59q4u xqj3whh x9f619 x1fv5t7x x5r8ks9 x1a11ngh"
    },
    1: {
      className: "x78zum5 x1a02dak x6s0dn4 x1cxx1dg x1wk51jd x1wxaq2x xp59q4u xqj3whh x9f619 xqhrb7l x163aph8 xphs7x8"
    }
  }[!!offline << 0]}>
      <Icon icon={ICONS[summary.kind]} size={20} />
      <span {...{
      0: {
        className: "x1iyjqo2 xeuugli x1j61zf2 x15i68ds x37zpob x1fzhlzt"
      },
      1: {
        className: "x1iyjqo2 xeuugli x1j61zf2 x37zpob x1fzhlzt x1cf7cre"
      }
    }[!!offline << 0]}>{rowText(summary)}</span>
      {summary.kind === "uploading" && <span className="xr0iwhn xhunfcg xkv8mnm xtvhhri xuxw1ft">{copy.photos.tile.percent(summary.percent)}</span>}
      {summary.kind === "failed" && <div className="x78zum5 xt4j9g6 x1qp53xl x8x9d4c x1x4vbq6">
          {summary.retryable && <Button variant="ghost" icon={RotateCw} onClick={onRetryAll}>
              {copy.photos.row.retryAll}
            </Button>}
          <Button variant="ghost" onClick={onClear}>
            {copy.photos.row.clear}
          </Button>
        </div>}
    </div>;
}
function uploadingText(summary: Extract<UploadSummary, {
  kind: "uploading";
}>): string {
  const running = summary.one ? copy.photos.row.uploadingOne(summary.one) : copy.photos.row.uploadingMany(summary.count);
  return summary.failed > 0 ? `${running} · ${copy.photos.row.failed(summary.failed)}` : running;
}
function rowText(summary: ShownSummary): string {
  switch (summary.kind) {
    case "offline":
      return summary.paused > 0 ? copy.photos.row.offlinePaused(summary.paused) : copy.photos.row.offline;
    case "failed":
      return summary.storageFull ? copy.photos.row.storageFull : copy.photos.row.failed(summary.failed);
    case "uploading":
      return uploadingText(summary);
  }
}